import { PageLayoutWithHelp } from "@features/page-help";
import { SupportDetailDrawer, SupportTicketDrawer } from "@features/support";
import type { SupportTicket } from "@features/support/api";
import { useSupportTicket, useSupportTickets } from "@features/support/api";
import { isAdminUser } from "@shared/rbac";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useStoredUser } from "@shared/useStoredUser";
import { Badge, Button, Select, Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

const statusColors: Record<string, string> = {
  new: "blue",
  accepted: "green",
  blocked: "orange",
  closed: "default",
};

const statusLabels: Record<string, string> = {
  new: "Новый",
  accepted: "В работе",
  blocked: "Заблокирован",
  closed: "Закрыт",
};

const SupportPage = () => {
  const user = useStoredUser();
  const admin = isAdminUser(user);
  const [statusFilter, setStatusFilter] = useState<string>();
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedId = searchParams.get("ticket_id") ?? undefined;

  const filters = useMemo(() => {
    const f: Record<string, string | undefined> = { status: statusFilter };
    if (!admin) f.created_by = user?.id;
    return f;
  }, [statusFilter, admin, user?.id]);

  const { data: tickets, isLoading } = useSupportTickets(filters);
  const ticketByIdQuery = useSupportTicket(selectedId);

  const selectedTicketFromList = useMemo(
    () => tickets?.find((t) => t.id === selectedId),
    [tickets, selectedId],
  );
  const selectedTicket = selectedTicketFromList ?? ticketByIdQuery.data;
  const hasActiveTicket = useMemo(
    () => (tickets ?? []).some((t) => t.status === "new" || t.status === "accepted"),
    [tickets],
  );

  const openTicket = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("ticket_id", id);
    setSearchParams(next);
  };

  const closeTicket = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("ticket_id");
    setSearchParams(next, { replace: true });
  };

  const columns: ColumnsType<SupportTicket> = [
    { title: "Тема", dataIndex: "title", render: (v: string) => v || "Без темы" },
    {
      title: "Статус",
      dataIndex: "status",
      width: 120,
      render: (v: string) => <Tag color={statusColors[v]}>{statusLabels[v] ?? v}</Tag>,
    },
    ...(admin
      ? [
          { title: "Автор", dataIndex: "creator_name" as const, width: 160 },
          { title: "Ответственный", dataIndex: "assignee_name" as const, width: 160 },
        ]
      : []),
    {
      title: "Непрочитано",
      dataIndex: "unread_count",
      width: 100,
      render: (v: number) => (v > 0 ? <Badge count={v} /> : null),
    },
    {
      title: "Обновлено",
      dataIndex: "updatedAt",
      width: 140,
      render: (v?: string) => (v ? dayjs(v).format("DD.MM.YY HH:mm") : "—"),
    },
  ];

  return (
    <PageLayoutWithHelp title="Поддержка">
      <Space style={{ marginBottom: 12 }}>
        <Select
          placeholder="Статус"
          allowClear
          style={{ width: 160 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))}
        />
        {!admin && !hasActiveTicket ? (
          <Button type="primary" onClick={() => setUserDrawerOpen(true)}>
            + чат
          </Button>
        ) : null}
      </Space>
      <EnterpriseTable
        columns={columns}
        dataSource={tickets ?? []}
        rowKey="id"
        loading={isLoading}
        onRow={(record) => ({ onClick: () => openTicket(record.id) })}
      />
      <SupportDetailDrawer
        ticket={selectedTicket}
        open={!!selectedId}
        onClose={closeTicket}
        isAdmin={admin}
      />
      {!admin ? (
        <SupportTicketDrawer open={userDrawerOpen} onClose={() => setUserDrawerOpen(false)} />
      ) : null}
    </PageLayoutWithHelp>
  );
};

export default SupportPage;
